import { useState, useRef, useEffect, useCallback } from "react";
import type { Message } from "../types/chat.types";
import { PHONE_MEDIA_QUERY } from "../utils/phoneKeyboard";
import { KB_HELD_VAR, KB_LIFT_EVENT } from "../pages/embedKeyboardRide";

/**
 * TRAC-497: Scroll-behavior machinery extracted from ChatInterface. Owns the
 * scroll container refs, the smart auto-scroll / scroll-to-latest functions,
 * the scroll-button state, and the initial-scroll state. Behavior lifted
 * verbatim from the post-merge ChatInterface.
 *
 * PARTIAL extraction by design: the four scroll EFFECTS (two TRAC-638
 * initial-scroll effects + the auto-scroll + re-anchor effects) intentionally
 * stay in ChatInterface, consuming the returns below. Moving them into this hook
 * (called high in the component) reordered them ahead of the load-from-storage
 * effect and left isProgrammaticScrollRef stuck true, breaking the
 * scroll-to-bottom button. Keeping them in place preserves effect order.
 */
/**
 * TRAC-862: extra scroll gap above the anchored message. Currently 0 — read why
 * before raising it.
 *
 * Anchoring a turn scrolls the viewport so the user's message is at the top. The
 * gap you then see below the embed's red banner is ~16px, and that is a CEILING,
 * not a preference: the PREVIOUS turn's feedback row (thumbs / copy / trace)
 * sits directly above the message, ending ~8px from it. Scrolling any further
 * back does not add whitespace — it pulls that row into view, which is the
 * "I can still see the feedback buttons" complaint this ticket started from.
 *
 * Measured on a real turn: at gap G the bubble's top lands at 66+G and the
 * feedback row's bottom at 50+G, so it stays hidden only while G <= 16 — and 16
 * is already contributed by the layout. Empirically: +8 (24px total) clips the
 * row; +32 (48px total) shows it outright.
 *
 * To genuinely get more air above an anchored turn, the whitespace has to BELONG
 * to that turn — raise the top margin on the last-turn wrapper and raise this by
 * the same amount, so the extra space is empty rather than occupied by the
 * previous turn. That changes inter-turn spacing everywhere, so it is a design
 * call, not a scroll fix.
 *
 * Exported because the user message is anchored from four places — this hook,
 * useChatStream's post-turn anchor (the one a NORMAL turn runs), escalate, and
 * the embed re-anchor. They must agree; a gap on some paths and not others is
 * worse than none.
 */
export const ANCHOR_TOP_GAP_PX = 0;

/**
 * TRAC-1050: how long the restore landing keeps watching for late height changes.
 *
 * A restored conversation is not finished rendering when the scroll happens: the
 * handoff poll delivers the rep's messages and the waiting card a beat later, and
 * images / source cards decode later still. Each of those grows the content AFTER
 * we have already landed, which is how the newest message ends up below the fold.
 * A short polled settle window re-measures and re-lands, then gets out of the way.
 *
 * Polled rather than ResizeObserver-driven on purpose: the observer only fires for
 * boxes it is given, and the growth here comes from several of them (the message
 * list, the activity slot, individual cards). One cheap measurement every tick
 * covers all of them, and it is deterministic to test.
 */
export const RESTORE_SETTLE_MS = 1200;
export const RESTORE_SETTLE_TICK_MS = 120;
/** Beyond this, the scroll moved because the CUSTOMER moved it — hands off. */
const RESTORE_USER_SCROLL_TOLERANCE_PX = 32;

/**
 * TRAC-1132: the phone keyboard and the last-turn spacer.
 *
 * A turn pins `minHeight = viewport` on the last-turn wrapper so the customer's
 * message can scroll to the top. That height is measured with the keyboard down. On
 * a phone the keyboard then takes 300+ px of the chat, the spacer is now taller than
 * the room, and a short text answer sits at the top with an empty gap down to the
 * message box (Chelsea, 2 Oct 2026). Card and picker answers are tall enough to hide
 * it. So on a phone, when the keyboard takes room, the spacer goes and the newest
 * message moves to sit right above the message box, the way iMessage keeps it.
 *
 * Phone = the phone media query alone (not the keyboard flag, which a wide touch
 * tablet can also raise). The keyboard is up when the loader says so
 * (`html[data-traci-kb=up]`, embedKeyboardBridge) or the chat lost at least
 * KEYBOARD_ROOM_MIN_PX of the tallest height it had at this width: more than the
 * mode banner squish or the retry indicator, less than any phone keyboard.
 */
export const KEYBOARD_ROOM_MIN_PX = 120;
const KEYBOARD_BOTTOM_SLOP_PX = 50;

export function isPhoneLayout(win: Window = window): boolean {
  try {
    return typeof win.matchMedia === "function" && win.matchMedia(PHONE_MEDIA_QUERY).matches;
  } catch {
    return false;
  }
}

/**
 * TRAC-1132 PR 2: iMessage scroll rules on phones (ALIGN rules 4 to 8).
 *
 * Desktop keeps the anchor-to-top framing (the customer's message at the top, a
 * viewport-tall spacer under it). A phone does not: the list is bottom-anchored the way
 * iMessage is. One function, settleScroll, decides where a phone's list sits; every
 * phone path (send, streamed text, cards, the typing indicator, a resize, restore, the
 * pill) goes through it, and none of them writes the spacer.
 *
 * Where the customer is, as one of four intents:
 * - turnTop: they just sent. Their message settles at the top of the chat (Dylan,
 *   2 Oct 11:08: the iMessage send animation ends with the message at the top) while
 *   the reply arrives under it. When the reply is in, the room under it closes: a
 *   reply that fits slides the view down so the newest line sits on the message box
 *   (rule 3, no gap), a taller one stays read from the top (rule 6). A chat that fits
 *   on screen never moves and never gets the spacer (rule 1).
 * - bottom: following the newest message. New content keeps them at the bottom.
 * - replyTop: the reply grew taller than the screen, so its first line was pinned to
 *   the top to be read from the start (rule 6). Nothing moves until they scroll.
 * - free: they scrolled up. Nothing moves; new messages raise a "New message" pill.
 */
export type PhonePin = "bottom" | "turnTop" | "replyTop" | "free";
/** One bottom slop for every phone decision; matches isScrolledToBottom. */
export const PHONE_BOTTOM_SLOP_PX = 50;
/** How long a smooth scroll is treated as in flight, so a resize re-targets it. */
const PHONE_SMOOTH_MS = 450;
/** TRAC-1176: a reply whose typing-done signal never comes counts as typed this long after the stream ends. */
const PHONE_TYPED_FALLBACK_MS = 15000;

export function useScrollBehavior(params: { messages: Message[]; isStreaming: boolean }) {
  const { messages, isStreaming } = params;

  const [isInitialScrollPending, setIsInitialScrollPending] = useState(false);
  const [pendingInitialScroll, setPendingInitialScroll] = useState(false);
  const [showScrollButton, setShowScrollButton] = useState<boolean>(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesAreaRef = useRef<HTMLDivElement>(null);
  const shouldAutoScrollRef = useRef<boolean>(true);
  const isProgrammaticScrollRef = useRef<boolean>(false);
  const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastScrollTargetRef = useRef<number | null>(null);
  const lastAssistantMsgRef = useRef<HTMLDivElement>(null);
  const lastUserMsgRef = useRef<HTMLDivElement>(null);
  const lastTurnWrapperRef = useRef<HTMLDivElement>(null);
  const lockAutoScrollRef = useRef<boolean>(false);
  const embedScrollRef = useRef<HTMLDivElement>(null);
  // TRAC-1050: the restore landing's settle window runs across renders, so it reads
  // the conversation through a ref — the array it closed over at the start is stale
  // by exactly the events (the handoff poll, a customer reply) it has to react to.
  const latestMessagesRef = useRef<Message[]>(messages);
  latestMessagesRef.current = messages;
  /** The settle window in flight, if any. At most one may ever be running. */
  const restoreSettleRef = useRef<(() => void) | null>(null);
  /** TRAC-1132: the scroll container's last seen geometry, and its tallest height at this width. */
  const roomRef = useRef({ w: 0, h: 0, tallest: 0 });
  // TRAC-1132 PR 2: phone scroll state (see PhonePin). The phone latch is read once and
  // re-read at every send and restore, so a turn never switches rules halfway through.
  const phoneLatchRef = useRef<boolean | null>(null);
  const pinRef = useRef<PhonePin>("bottom");
  const replyArmedRef = useRef(false);
  const seenCountRef = useRef(0);
  const sendSlideUntilRef = useRef(0);
  const turnPlacedRef = useRef(false);
  const landRef = useRef<(() => void) | null>(null);
  const hasNewRef = useRef(false);
  // A finger (or wheel) is driving the list: settleScroll keeps its hands off until the
  // fling ends, and handleScroll reads the intent even while a programmatic flag is up.
  const touchingRef = useRef(false);
  const userScrollUntilRef = useRef(0);
  const smoothFollowUpRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastPhoneScrollTopRef = useRef(0);
  const isStreamingRef = useRef(isStreaming);
  /** Latest settleScroll, for timers and observers that outlive a render. */
  const settleRef = useRef<(prevClientHeight?: number) => void>(() => {});
  isStreamingRef.current = isStreaming;
  // TRAC-1176: the typewriter shows the reply 1 to 2 s after the stream ends. "The reply
  // is in" (rule 3 vs rule 6) waits for it, or a long reply is judged by its first lines.
  const typedRef = useRef(true);
  if (isStreaming) typedRef.current = false;
  const smoothUntilRef = useRef(0);
  const [hasNewMessage, setHasNewMessage] = useState(false);

    // Scroll container is the embed wrapper div (iframe-scoped).
    const getScrollContainer = (): HTMLElement | null => embedScrollRef.current;
    const getScrollTop = (): number => {
      return getScrollContainer()?.scrollTop ?? 0;
    };
    const getScrollHeight = (): number => {
      return getScrollContainer()?.scrollHeight ?? 0;
    };
    const getViewportHeight = (): number => {
      return getScrollContainer()?.clientHeight ?? 0;
    };
    const doScrollTo = (options: ScrollToOptions): void => {
      getScrollContainer()?.scrollTo(options);
    };
    const getElementScrollOffset = (el: HTMLElement): number => {
      const container = getScrollContainer();
      if (!container) return el.offsetTop;
      return el.getBoundingClientRect().top + container.scrollTop - container.getBoundingClientRect().top;
    };

    /** TRAC-1132: a phone with its on-screen keyboard taking room from the chat. */
    const phoneKeyboardUp = (): boolean => {
      if (!isPhoneLayout()) return false;
      if (document.documentElement.getAttribute("data-traci-kb") === "up") return true;
      // Fallback for when the loader's flag lags: only with the message box focused,
      // so an address-bar collapse or a banner squish never reads as a keyboard.
      const active = document.activeElement;
      if (!active || !/^(TEXTAREA|INPUT)$/.test(active.tagName)) return false;
      const room = roomRef.current;
      const c = getScrollContainer();
      return !!c && room.tallest > 0 && c.clientHeight > 0 && room.tallest - c.clientHeight >= KEYBOARD_ROOM_MIN_PX;
    };

    /**
     * TRAC-1132: the chat just got shorter on a phone. If the keyboard took the room
     * and the customer was looking at the last turn (anchored on it, or at the
     * bottom), drop the spacer and put the newest message right above the message
     * box. A customer who scrolled up to older messages is left where they are.
     * Returns true when it moved the view.
     */
    const fitLastTurnToKeyboard = (): boolean => {
      const c = getScrollContainer();
      if (!c) return false;
      const room = roomRef.current;
      const prev = { ...room };
      const w = c.clientWidth, h = c.clientHeight;
      if (w !== room.w) room.tallest = 0;   // rotated or resized: start over at this width
      room.w = w; room.h = h; room.tallest = Math.max(room.tallest, h);
      if (!isPhoneLayout() || h === 0 || prev.h === 0 || h >= prev.h || w !== prev.w) return false;
      // TRAC-1176: the turn just sent belongs to the phone rules while it settles (turnTop:
      // the question stays at the top) or is read from its first line (replyTop). Its
      // spacer is not "the bottom" (rules 13, 24).
      if (pinRef.current === "turnTop" || pinRef.current === "replyTop") return false;
      if (!phoneKeyboardUp()) return false;
      const wrapper = lastTurnWrapperRef.current;
      const anchor = lastUserMsgRef.current;
      // A shrink leaves scrollTop and scrollHeight alone, so the live values still
      // say where the customer was (they may have scrolled since the last resize).
      const top = c.scrollTop;
      const wasAtBottom = c.scrollHeight - top - prev.h <= KEYBOARD_BOTTOM_SLOP_PX;
      const wasOnLastTurn = !!anchor && top >= getElementScrollOffset(anchor) - ANCHOR_TOP_GAP_PX - KEYBOARD_BOTTOM_SLOP_PX;
      if (!wasAtBottom && !wasOnLastTurn) return false;
      if (!wasAtBottom) {
        // Anchored on the last turn: act only when the spacer leaves empty room in
        // the smaller view. A long answer read from its top fills the view; leave it.
        // The spacer counts as content, so measure with it cleared (one reflow, no paint).
        const pinned = wrapper ? wrapper.style.minHeight : "";
        if (wrapper && pinned) wrapper.style.minHeight = "";
        const endMark = messagesEndRef.current;
        const contentBottom = endMark ? getElementScrollOffset(endMark) : c.scrollHeight;
        if (contentBottom > top + h) {
          if (wrapper && pinned) wrapper.style.minHeight = pinned;
          return false;
        }
      }
      if (wrapper && wrapper.style.minHeight) wrapper.style.minHeight = "";
      const bottom = Math.max(0, c.scrollHeight - c.clientHeight);
      if (Math.abs(c.scrollTop - bottom) < 1) return false;
      isProgrammaticScrollRef.current = true;
      setShowScrollButton(false);
      doScrollTo({ top: bottom, behavior: "auto" });
      lastScrollTargetRef.current = bottom;
      scheduleProgrammaticScrollEnd(300);
      return true;
    };

    /**
     * TRAC-1132 PR 2: the vehicle picker hides the list (display:none), which drops its
     * scroll position. A customer who was reading higher up comes back to that spot;
     * one following the bottom is put back there by settleScroll.
     */
    const restoreAfterUnhide = (prevClientHeight: number): void => {
      const c = getScrollContainer();
      if (!c || !isPhoneTurn() || prevClientHeight !== 0 || c.clientHeight === 0) return;
      if (pinRef.current === "bottom" || c.scrollTop !== 0 || lastPhoneScrollTopRef.current <= 0) return;
      isProgrammaticScrollRef.current = true;
      doScrollTo({ top: lastPhoneScrollTopRef.current, behavior: "auto" });
      lastScrollTargetRef.current = lastPhoneScrollTopRef.current;
      scheduleProgrammaticScrollEnd(300);
    };

    /** TRAC-1132 PR 2: do the phone rules own this turn? Latched; see phoneLatchRef. */
    const isPhoneTurn = (): boolean => {
      if (phoneLatchRef.current === null) phoneLatchRef.current = isPhoneLayout();
      return phoneLatchRef.current;
    };

    const phoneScrollTo = (top: number, smooth: boolean, onLand?: () => void): void => {
      landRef.current = onLand ?? null;
      isProgrammaticScrollRef.current = true;
      doScrollTo({ top, behavior: smooth ? "smooth" : "auto" });
      lastScrollTargetRef.current = top;
      lastPhoneScrollTopRef.current = top;
      if (smooth) {
        // One slide per send. Anything that moves the bottom while it runs (the banner
        // squish, the reply starting) is caught by a single instant correction when the
        // slide is done, instead of restarting the animation every frame.
        smoothUntilRef.current = Date.now() + PHONE_SMOOTH_MS;
        if (smoothFollowUpRef.current) clearTimeout(smoothFollowUpRef.current);
        smoothFollowUpRef.current = setTimeout(() => {
          smoothFollowUpRef.current = null;
          const land = landRef.current;
          landRef.current = null;
          land?.();
          settleRef.current();
        }, PHONE_SMOOTH_MS + 20);
      } else if (onLand) {
        landRef.current = null;
        onLand();
      }
      scheduleProgrammaticScrollEnd(smooth ? 1000 : 300);
    };

    const userIsScrolling = (): boolean => touchingRef.current || Date.now() < userScrollUntilRef.current;

    /** The customer is following the newest message: nothing unread, no pill. */
    const markPhoneBottom = (): void => {
      pinRef.current = "bottom";
      shouldAutoScrollRef.current = true;
      seenCountRef.current = latestMessagesRef.current.length;
      hasNewRef.current = false;
      setHasNewMessage(false);
      setShowScrollButton(false);
    };

    /**
     * TRAC-1132 PR 2: a send (typed, card, escalate) on a phone. Called BEFORE the
     * customer's message is added. Desktop: returns false and changes nothing, so the
     * caller runs today's anchor-to-top path. Phone: the customer acted, so wherever
     * they were (scrolled up too, rule 5) their message slides once to the top of the
     * chat (rule 4) and settleScroll owns the turn from there (see PhonePin turnTop).
     */
    const beginPhoneSend = (): boolean => {
      phoneLatchRef.current = isPhoneLayout();
      if (!phoneLatchRef.current) return false;
      lockAutoScrollRef.current = false;
      replyArmedRef.current = true;
      sendSlideUntilRef.current = Date.now() + PHONE_SMOOTH_MS + 300;
      markPhoneBottom();
      pinRef.current = "turnTop";
      turnPlacedRef.current = false;
      return true;
    };

    /**
     * TRAC-1132 PR 2: the turn the customer just sent (PhonePin turnTop). Measures with
     * the spacer cleared (one reflow, no paint) and puts it back only while it is needed.
     */
    /**
     * TRAC-1132 PR 2: how much of the list the customer can see. On iPhone the keyboard
     * ride (TRAC-1152, HOLD) keeps the list full height with the keyboard up and lifts the
     * message box over it, so the room ends at the box's top, not at the list's bottom.
     */
    const visibleRoom = (c: HTMLElement): number => {
      const h = c.clientHeight;
      if (document.documentElement.getAttribute("data-traci-kb") !== "up") return h;
      const ta = c.closest('[data-traci-kb-part="widget"]')?.querySelector("textarea");
      if (!ta) return h;
      let a: HTMLElement = c;
      while (a.parentElement && !a.parentElement.contains(ta)) a = a.parentElement;
      const box = a.nextElementSibling;
      if (!box) return h;
      // TRAC-1176: the room once the box has risen, read from the layout (offsetTop ignores the ride's transforms) and
      // the ride's target lift, so a settle mid-rise sizes the spacer once instead of chasing the moving box.
      const held = parseFloat(document.documentElement.style.getPropertyValue(KB_HELD_VAR));
      const room = held > 0 && box instanceof HTMLElement
        ? box.offsetTop - a.offsetTop - (c.getBoundingClientRect().top - a.getBoundingClientRect().top) - held
        : box.getBoundingClientRect().top - c.getBoundingClientRect().top;
      return room > 0 ? Math.min(h, room) : h;
    };

    /**
     * TRAC-1176: the iPhone keyboard ride is about to slide the box up (KB_LIFT_EVENT, same task). A turn spacer takes
     * its final room now and the list scrolls back by the lift; the ride holds the content up by however far scrollTop
     * moved, so the question never moves (sizing the spacer mid-rise clamped scrollTop while the box was still moving).
     */
    const fitTurnToLift = (): void => {
      const c = getScrollContainer();
      const wrapper = lastTurnWrapperRef.current;
      if (!c || !wrapper || !wrapper.style.minHeight || pinRef.current !== "turnTop" || !isPhoneTurn()) return;
      const held = parseFloat(document.documentElement.style.getPropertyValue(KB_HELD_VAR));
      if (!(held > 0)) return;
      const s = c.scrollTop;
      wrapper.style.minHeight = `${visibleRoom(c)}px`;
      c.scrollTop = Math.max(0, s - held);
    };

    /**
     * TRAC-1132 PR 2: the turn's spacer outlived its turn (the customer dragged away from
     * the question while the reply came in). Drop it at rest; if they are looking at the
     * empty room it leaves, slide the newest line down onto the box first (rule 3).
     * Returns true when it started a slide.
     */
    const releaseSpacer = (c: HTMLElement): boolean => {
      const wrapper = lastTurnWrapperRef.current;
      if (!wrapper || !wrapper.style.minHeight || pinRef.current === "turnTop") return false;
      const s = c.scrollTop;
      const pinned = wrapper.style.minHeight;
      wrapper.style.minHeight = "";
      const end = c.scrollHeight;
      const max = Math.max(0, end - c.clientHeight);
      if (max >= s - 1) return false;   // nothing on screen moves
      wrapper.style.minHeight = pinned;
      // TRAC-1176: iPhone keyboard (HOLD): a reply read from its first line that reaches the
      // box leaves its empty room behind the keyboard. Keep it until the keyboard closes.
      if (pinRef.current === "replyTop" && end >= s + visibleRoom(c)) return false;
      markPhoneBottom();
      phoneScrollTo(max, true, () => {
        if (wrapper.style.minHeight) wrapper.style.minHeight = "";
      });
      return true;
    };

    const settlePhoneTurn = (c: HTMLElement): void => {
      const ask = lastUserMsgRef.current;
      const wrapper = lastTurnWrapperRef.current;
      if (!ask) { pinRef.current = "bottom"; return; }
      const h = visibleRoom(c);
      const pinned = wrapper ? wrapper.style.minHeight : "";
      if (wrapper && pinned) wrapper.style.minHeight = "";
      const endMark = messagesEndRef.current;
      const contentBottom = endMark ? getElementScrollOffset(endMark) : c.scrollHeight;
      // The list's own end; with the keyboard held the ride lifts that end onto the box.
      const bottomNoSpacer = Math.max(0, c.scrollHeight - c.clientHeight);
      const askTop = Math.max(0, getElementScrollOffset(ask) - ANCHOR_TOP_GAP_PX);
      const reply = lastAssistantMsgRef.current;
      const replyIn = !isStreamingRef.current && typedRef.current && !!reply && getElementScrollOffset(reply) > getElementScrollOffset(ask);
      const slide = Date.now() < sendSlideUntilRef.current;
      sendSlideUntilRef.current = 0;
      // Rule 1: the whole chat fits on screen. No spacer, nothing moves; if the reply
      // later outgrows the screen the bottom rules (and rule 6) take it from here.
      if (!turnPlacedRef.current && contentBottom <= h) {
        pinRef.current = "bottom";
        return;
      }
      turnPlacedRef.current = true;
      if (replyIn) {
        replyArmedRef.current = false;
        if (contentBottom - askTop > h) {
          // Rule 6: the turn is taller than the screen; it is read from the top. The
          // content fills the view, so dropping the spacer moves nothing.
          pinRef.current = "replyTop";
          lastScrollTargetRef.current = askTop;
          // TRAC-1176: iPhone keyboard (HOLD): the list keeps its full height, so a turn taller
          // than the room above the box can be shorter than the list. Keep the spacer then
          // (its room is behind the keyboard); releaseSpacer drops it once the keyboard closes.
          if (wrapper && pinned && bottomNoSpacer < c.scrollTop - 1) wrapper.style.minHeight = pinned;
          return;
        }
        // Rule 3: a reply that fits leaves room under it. Slide down until the newest
        // line sits on the message box, then drop the spacer (no visible change then).
        const target = bottomNoSpacer;
        markPhoneBottom();
        if (Math.abs(c.scrollTop - target) < 1) return;
        if (wrapper && pinned) wrapper.style.minHeight = pinned;
        phoneScrollTo(target, true, () => {
          if (wrapper && wrapper.style.minHeight) wrapper.style.minHeight = "";
        });
        return;
      }
      // The reply is still coming: keep the question at the top with room under it.
      if (wrapper) wrapper.style.minHeight = `${h}px`;
      if (Math.abs(c.scrollTop - askTop) >= 1) phoneScrollTo(askTop, slide);
    };

    /**
     * TRAC-1132 PR 2: the one owner of a phone's scroll position. Called when the
     * conversation changes, when content grows (images, cards, typing indicator) and
     * when the chat is resized (keyboard, banner, address bar). `prevClientHeight` is
     * the chat's height before a resize: whether the customer was at the bottom is
     * judged against the view they were looking at, not the new one.
     */
    const settleScroll = (prevClientHeight?: number): void => {
      const c = getScrollContainer();
      if (!c || !isPhoneTurn()) return;
      // The end-of-chat framing (TRAC-732) and a restore still landing hold the lock.
      if (lockAutoScrollRef.current) return;
      // Hidden (the vehicle picker hides the list): nothing to measure.
      if (c.clientHeight === 0) return;
      const count = latestMessagesRef.current.length;
      // The opening screen: nothing to follow, and its no-scroll wrapper (TRAC-797)
      // could not be scrolled back by the customer.
      if (count === 0 || window.getComputedStyle(c).overflowY === "hidden") return;
      // The customer's finger has the list; their scroll decides (handleScroll).
      if (userIsScrolling()) return;
      // A send's slide is still running; its follow-up corrects the landing once.
      if (Date.now() < smoothUntilRef.current) return;
      if (count < seenCountRef.current) markPhoneBottom();   // conversation cleared or replaced
      if (releaseSpacer(c)) return;
      if (pinRef.current === "free" && prevClientHeight
          && c.scrollHeight - c.scrollTop - prevClientHeight <= PHONE_BOTTOM_SLOP_PX) {
        pinRef.current = "bottom";
      }
      if (pinRef.current === "turnTop") { settlePhoneTurn(c); return; }
      if (pinRef.current === "replyTop") return;
      if (pinRef.current === "free") {
        if (count > seenCountRef.current) {
          hasNewRef.current = true;
          setHasNewMessage(true);
          setShowScrollButton(true);
        }
        return;
      }
      const bottom = Math.max(0, c.scrollHeight - c.clientHeight);
      let target = bottom;
      const reply = lastAssistantMsgRef.current;
      const ask = lastUserMsgRef.current;
      if (replyArmedRef.current && reply && ask) {
        // Rule 6: a reply taller than the screen is read from its first line.
        const replyTop = getElementScrollOffset(reply);
        const isReply = replyTop > getElementScrollOffset(ask);
        if (isReply && bottom > replyTop + 1) {
          target = Math.max(0, replyTop);
          pinRef.current = "replyTop";
          replyArmedRef.current = false;
        } else if (isReply && !isStreamingRef.current) {
          // The reply is in and it fits. Later growth (a card image, the feedback row)
          // must not snap a reader at the bottom back up to its first line.
          replyArmedRef.current = false;
        }
      }
      if (pinRef.current === "bottom") seenCountRef.current = count;
      if (Math.abs(c.scrollTop - target) < 1) return;
      const now = Date.now();
      // A send slides once (rule 4); everything else follows instantly.
      const slide = now < sendSlideUntilRef.current;
      sendSlideUntilRef.current = 0;
      phoneScrollTo(target, slide);
    };

    /**
     * Smart auto-scroll that keeps the top of the latest assistant message visible.
     *
     * - For user messages / loading indicator: scrolls to the absolute bottom
     * - For assistant messages streaming in: scrolls to bottom while the message
     *   fits in the viewport, then caps at the message top once it overflows
     *   so the beginning of the response stays visible
     */
    /**
     * Clear programmatic scroll flag safely — cancels any pending timeout
     * and sets a new one. Prevents stale timeouts from racing with active scrolls.
     */
    const scheduleProgrammaticScrollEnd = (delayMs: number): void => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      scrollTimeoutRef.current = setTimeout(() => {
        isProgrammaticScrollRef.current = false;
        scrollTimeoutRef.current = null;
      }, delayMs);
    };

    const scrollToLatestMessage = (behavior: ScrollBehavior = "auto"): void => {
      // TRAC-1132 PR 2: a phone never anchors the turn to the top or writes the spacer.
      if (isPhoneTurn()) { settleScroll(); return; }
      const lastMsg = lastAssistantMsgRef.current;

      isProgrammaticScrollRef.current = true;
      setShowScrollButton(false);
      shouldAutoScrollRef.current = true;


      // Pin to top of assistant message only while actively streaming.
      // On page load or post-stream we scroll to last user message instead.
      const lastMessage = messages[messages.length - 1];
      const isAssistantStreaming =
        isStreaming && lastMessage?.role === "assistant" && lastMsg;

      if (isAssistantStreaming) {
        const msgTop = getElementScrollOffset(lastMsg);
        const maxScroll = msgTop;
        const bottomScroll = getScrollHeight() - getViewportHeight();
        const targetScroll = Math.min(bottomScroll, maxScroll);

        doScrollTo({ top: targetScroll, behavior });
        lastScrollTargetRef.current = targetScroll;
      } else if (lastUserMsgRef.current) {
        // Scroll to last user msg regardless of what role the final message is.
        // TRAC-478: Measure inside rAF so the textarea-resize useEffect (declared
        // later and therefore fires after this one) has had a chance to shrink the
        // textarea from its pre-conversation 3-line min-height to its final
        // post-conversation size. Measuring synchronously here would read the
        // scroll container with a stale-too-tall textarea eating ~42px of it and
        // leave the wrapper short on reload. Second rAF waits for the minHeight
        // reflow before computing the scroll target.
        const userMsg = lastUserMsgRef.current;
        const wrapper = lastTurnWrapperRef.current;
        requestAnimationFrame(() => {
          if (wrapper) {
            // Use full clientHeight (not subtracting padding) — the padding is inside the
            // scroll container so we need minHeight to match the full container height to
            // guarantee enough scrollHeight to reach the user message.
            const vh = getViewportHeight();
            wrapper.style.minHeight = `${vh}px`;
          }
          requestAnimationFrame(() => {
            const targetScroll = Math.max(0, getElementScrollOffset(userMsg) - ANCHOR_TOP_GAP_PX);
            doScrollTo({ top: targetScroll, behavior: "smooth" });
            lastScrollTargetRef.current = null;
            shouldAutoScrollRef.current = false;
            lockAutoScrollRef.current = true;
            scheduleProgrammaticScrollEnd(1000);

            // The two rAFs above cover the textarea settle, but NOT a CSS
            // transition: the mode banner squishes over 200ms on send, growing the
            // container after we measured it. That leaves minHeight short, so the
            // scroll above clamps early and the previous turn stays peeking at the
            // top. Watch for late resizes, re-pin minHeight to the live height and
            // re-anchor. Short-lived on purpose — it lives inside this scroll (no
            // useEffect) so it can't reorder the effects this hook deliberately
            // leaves in ChatInterface, and it stops after the settle window so it
            // never yanks a customer who has started scrolling.
            const container = getScrollContainer();
            if (container && typeof ResizeObserver !== "undefined") {
              let settled = 0;
              const ro = new ResizeObserver(() => {
                if (!wrapper) return;
                // TRAC-1132: while the keyboard holds the room, fitLastTurnToKeyboard owns
                // it. Keep watching: when the keyboard closes and the chat grows back,
                // this re-pins the spacer so the new message still reaches the top.
                if (phoneKeyboardUp()) return;
                const vhNow = container.clientHeight;
                if (`${vhNow}px` === wrapper.style.minHeight) return; // no-op resize
                wrapper.style.minHeight = `${vhNow}px`;
                doScrollTo({ top: Math.max(0, getElementScrollOffset(userMsg) - ANCHOR_TOP_GAP_PX), behavior: "smooth" });
                settled = window.setTimeout(() => ro.disconnect(), 400);
              });
              ro.observe(container);
              window.setTimeout(() => { ro.disconnect(); if (settled) clearTimeout(settled); }, 1000);
            }
          });
        });
        // Return early — scheduleProgrammaticScrollEnd called inside rAF above
        return;
      } else {
        const bottomScroll = getScrollHeight() - getViewportHeight();
        doScrollTo({ top: bottomScroll, behavior });
        lastScrollTargetRef.current = bottomScroll;
      }

      scheduleProgrammaticScrollEnd(behavior === "smooth" ? 1000 : 500);
    };

    /**
     * TRAC-1050: where a RESTORED conversation lands after a refresh or a move to
     * another page. Called once by the TRAC-638 restore effect in ChatInterface.
     *
     * That effect used to anchor the last USER message to the top of the viewport,
     * unconditionally — the same framing a live turn gets, where "your question at
     * the top, the answer under it" is exactly right. On a restore it is only right
     * while the tail BELOW that message fits on screen. In a rep conversation the
     * tail is the whole rep exchange (and the queue/waiting card, which the handoff
     * poll renders after we have already scrolled), so the newest lines sat below
     * the fold and the customer came back to old history — including a previous
     * chat's "Back with the AI tire agent" line, which reads as "you got cut off".
     * Nothing rep-specific about it: one long restored answer does the same.
     *
     * So: anchor when the tail fits, otherwise land on the newest message. Then
     * hold that landing through the settle window while late content arrives, and
     * stop the moment the customer scrolls — coming back to where you left off is
     * the point, and yanking someone who has started reading would undo it.
     *
     * The anchor -> bottom switch is one-way inside a single landing: content that
     * grows past the viewport moves us to the newest message, but content that
     * shrinks again (the typing indicator clearing) must not jump the view back up.
     *
     * Returns a cleanup that stops the settle loop (unmount / navigation mid-window).
     */
    const scrollRestoredHistoryIntoView = (): (() => void) => {
      const noop = (): void => {};
      if (!getScrollContainer()) return noop;

      // Only one landing at a time. A second one starting while the first window is
      // still open would leave two loops writing the same scrollTop and the same
      // wrapper headroom, and the loser would win every other tick.
      restoreSettleRef.current?.();

      // TRAC-1132 PR 2: a phone opens at the newest message (rule 7), with no spacer.
      phoneLatchRef.current = isPhoneLayout();
      const phone = phoneLatchRef.current;
      if (phone) {
        lockAutoScrollRef.current = false;
        replyArmedRef.current = false;
        markPhoneBottom();
      }

      let landedAt = 0;
      let pinningBottom = phone;
      const countUserTurns = (list: Message[]): number =>
        list.reduce((n, m) => (m.role === "user" ? n + 1 : n), 0);
      const userTurnsAtLanding = countUserTurns(messages);

      // Is everything after the customer's last message just that turn's own answer?
      // Then TRAC-638's framing is still the right one even when the answer runs
      // longer than the screen: you read an answer from its start, and landing on
      // its LAST line instead would be a quiet regression of a shipped decision.
      // A rep exchange, a queue band or a divider is not that — it is newer
      // conversation the anchored turn does not own, and it has to be in view.
      //
      // Read live on every placement, not once: the handoff poll can turn a tail
      // that WAS the turn's own answer ("Connecting you to a Tire Expert now…")
      // into a rep exchange while this window is still open.
      const tailIsOwnAnswer = (): boolean => {
        const list = latestMessagesRef.current;
        let lastUserIdx = -1;
        for (let i = list.length - 1; i >= 0; i--) {
          if (list[i].role === "user") { lastUserIdx = i; break; }
        }
        if (lastUserIdx < 0) return false;
        const tail = list.slice(lastUserIdx + 1);
        return tail.length === 1 && !tail[0].responder;
      };

      const applyScroll = (top: number): void => {
        isProgrammaticScrollRef.current = true;
        setShowScrollButton(false);
        doScrollTo({ top, behavior: "auto" });
        lastScrollTargetRef.current = top;
        landedAt = top;
        scheduleProgrammaticScrollEnd(500);
      };

      const place = (): void => {
        const wrapper = lastTurnWrapperRef.current;
        // Measure with no artificial headroom in the way. The anchor idiom pins a
        // viewport-tall minHeight on the last-turn wrapper; left in place it reads
        // back as content and makes a short tail look like a tall one. Clearing and
        // restoring it inside one synchronous pass costs a reflow, not a repaint,
        // so the customer never sees the intermediate layout.
        if (wrapper && wrapper.style.minHeight) wrapper.style.minHeight = "";

        const endMark = messagesEndRef.current;
        const contentBottom = endMark ? getElementScrollOffset(endMark) : getScrollHeight();
        const anchor = lastUserMsgRef.current;
        const anchorTop = anchor
          ? Math.max(0, getElementScrollOffset(anchor) - ANCHOR_TOP_GAP_PX)
          : null;
        const viewport = getViewportHeight();

        // No user turn at all (a restored queue wait, a rep-only tail) used to mean
        // no scroll whatsoever, which left the container at scrollTop 0 — the literal
        // top of the history. That is the worst version of this bug, so it lands too.
        // Strictly greater: a tail measuring exactly one viewport still fits, so it
        // keeps the anchor and its last line sits flush with the bottom edge.
        // TRAC-1132: with the phone keyboard up there is no room for the anchor's spacer;
        // the newest message belongs right above the message box.
        if (anchorTop === null || pinningBottom || phoneKeyboardUp()
            || (contentBottom - anchorTop > viewport && !tailIsOwnAnswer())) {
          pinningBottom = true;
          applyScroll(Math.max(0, getScrollHeight() - getViewportHeight()));
          return;
        }

        if (wrapper) wrapper.style.minHeight = `${viewport}px`;
        applyScroll(anchorTop);
      };

      place();
      // TRAC-1132 PR 2: on a phone the settle window is not needed; late content
      // (the handoff poll, images) reaches settleScroll through the observer and the
      // messages effect, so there is one owner instead of two.
      if (phone) return noop;

      let elapsed = 0;
      let timer: ReturnType<typeof setTimeout> | null = null;
      const stop = (): void => {
        if (timer) clearTimeout(timer);
        timer = null;
        if (restoreSettleRef.current === stop) restoreSettleRef.current = null;
      };
      const tick = (): void => {
        timer = setTimeout(() => {
          timer = null;
          elapsed += RESTORE_SETTLE_TICK_MS;
          const container = getScrollContainer();
          if (!container) return stop();
          // The scroll sits somewhere we did not put it: the customer took over.
          if (Math.abs(container.scrollTop - landedAt) > RESTORE_USER_SCROLL_TOLERANCE_PX) return stop();
          // The customer has said something. Restoring is over and the live-turn
          // framing (scrollToLatestMessage) owns the scroll from here — two owners
          // is how you get a view that fights the person using it.
          if (countUserTurns(latestMessagesRef.current) > userTurnsAtLanding) return stop();
          place();
          if (elapsed < RESTORE_SETTLE_MS) tick();
          else stop();
        }, RESTORE_SETTLE_TICK_MS);
      };
      tick();

      restoreSettleRef.current = stop;
      return stop;
    };

    /**
     * Scroll to the absolute bottom of the chat (used by scroll-to-bottom button).
     */
    const scrollToBottom = (behavior: ScrollBehavior = "auto"): void => {
      // TRAC-1132 PR 2: on a phone the pill scrolls the chat itself (scrollIntoView can
      // also pan the page around the frame) and puts the customer back on the bottom.
      if (isPhoneTurn()) {
        const c = getScrollContainer();
        lockAutoScrollRef.current = false;
        replyArmedRef.current = false;
        markPhoneBottom();
        if (c) phoneScrollTo(Math.max(0, c.scrollHeight - c.clientHeight), behavior === "smooth");
        return;
      }
      const container = messagesAreaRef.current;
      isProgrammaticScrollRef.current = true;
      setShowScrollButton(false);
      lockAutoScrollRef.current = false;
      shouldAutoScrollRef.current = true;
      messagesEndRef.current?.scrollIntoView({ behavior });

      lastScrollTargetRef.current = getScrollHeight() - getViewportHeight();

      scheduleProgrammaticScrollEnd(behavior === "smooth" ? 1000 : 500);
    };

    /**
     * Check if scrolled to bottom
     */
    const isScrolledToBottom = (): boolean => {
      if (!messagesAreaRef.current) return true;
      return getScrollHeight() - getScrollTop() <= getViewportHeight() + 50;
    };

    /**
     * Check if scroll position is near our last programmatic target.
     * This accounts for the pinning behavior where we cap scroll at the
     * message top rather than the absolute bottom.
     */
    const isNearScrollTarget = (): boolean => {
      if (lastScrollTargetRef.current === null) return false;
      return Math.abs(getScrollTop() - lastScrollTargetRef.current) <= 50;
    };

    /**
     * Handle scroll events
     */
    const handleScroll = (): void => {
      const atBottom = isScrolledToBottom();
      const atTarget = isNearScrollTarget();

      // TRAC-1132 PR 2: on a phone a scroll the customer drives always counts, even
      // while a programmatic flag is up (streaming re-arms it every chunk, which would
      // otherwise swallow the scroll and pull the reader back down on the next chunk).
      if (isPhoneTurn() && userIsScrolling()) {
        isProgrammaticScrollRef.current = false;
        userScrollUntilRef.current = Math.max(userScrollUntilRef.current, Date.now() + 300);
        lastPhoneScrollTopRef.current = getScrollTop();
        if (pinRef.current === "turnTop" && atTarget) return;
        replyArmedRef.current = false;
        if (atBottom) {
          markPhoneBottom();
        } else if (pinRef.current === "replyTop" && atTarget) {
          setShowScrollButton(hasNewRef.current);
        } else {
          pinRef.current = "free";
          shouldAutoScrollRef.current = false;
          setShowScrollButton(hasNewRef.current || getScrollHeight() - getScrollTop() - getViewportHeight() > 600);
        }
        return;
      }

      if (isProgrammaticScrollRef.current) {
        if (atBottom || atTarget) {
          isProgrammaticScrollRef.current = false;
        }
        return;
      }

      // TRAC-1132 PR 2: a phone scroll nobody's finger drove (momentum after the
      // fling window, an iOS clamp) is judged by position alone, the same way.
      if (isPhoneTurn()) {
        lastPhoneScrollTopRef.current = getScrollTop();
        if (atBottom && pinRef.current !== "turnTop") markPhoneBottom();
        return;
      }

      // User scroll handling — being at either the bottom OR
      // our pinned target counts as "in position" for auto-scroll.
      // lockAutoScrollRef prevents false re-enables when short content makes
      // the user-message position appear to be "at bottom".
      if (atBottom || atTarget) {
        if (!lockAutoScrollRef.current) {
          shouldAutoScrollRef.current = true;
        }
        setShowScrollButton(false);
      } else {
        // User scrolled away — disable auto-scroll but preserve the lock.
        // Lock is only cleared by explicit actions (scroll-to-bottom button,
        // new message send, session clear) so streaming can't re-engage auto-scroll.
        shouldAutoScrollRef.current = false;
        // Only show scroll button if >200px from bottom
        setShowScrollButton(getScrollHeight() - getScrollTop() - getViewportHeight() > 600);
      }
    };

    /**
     * Handle click on scroll-to-bottom button
     */
    const handleScrollButtonClick = (): void => {
      scrollToBottom("smooth");
    };

  // TRAC-1132: watch the scroll container's size for the phone keyboard. The observer
  // never scrolls when it is attached, only when the chat later gets shorter on a
  // phone, so it cannot reorder the scroll effects that stay in ChatInterface. The
  // container can mount after this hook, so every render checks it is watching the
  // current one.
  //
  // TRAC-1132 PR 2: the same observer also watches the message list, so content that
  // grows after render (images, cards, the typing indicator) and every resize reach
  // settleScroll on a phone. PR 1's keyboard fit runs first and keeps its job.
  const fitRef = useRef(fitLastTurnToKeyboard);
  fitRef.current = fitLastTurnToKeyboard;
  settleRef.current = settleScroll;
  /** TRAC-1176: MessageRenderer's onTypingSettled: the newest reply is fully shown. */
  const noteTypingSettled = useCallback((): void => {
    if (isStreamingRef.current || typedRef.current) return;
    typedRef.current = true;
    settleRef.current();
  }, []);
  useEffect(() => {
    if (isStreaming) return;
    const t = setTimeout(noteTypingSettled, PHONE_TYPED_FALLBACK_MS);
    return () => clearTimeout(t);
  }, [isStreaming, noteTypingSettled]);
  const userIsScrollingRef = useRef(userIsScrolling);
  userIsScrollingRef.current = userIsScrolling;
  const fitTurnToLiftRef = useRef(fitTurnToLift);
  fitTurnToLiftRef.current = fitTurnToLift;
  const unhideRef = useRef(restoreAfterUnhide);
  unhideRef.current = restoreAfterUnhide;
  const roomObserverRef = useRef<{ ro: ResizeObserver; el: HTMLElement; list: HTMLElement | null } | null>(null);
  useEffect(() => {
    const el = embedScrollRef.current;
    const list = messagesAreaRef.current;
    if (roomObserverRef.current?.el === el && roomObserverRef.current?.list === list) return;
    roomObserverRef.current?.ro.disconnect();
    roomObserverRef.current = null;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => {
      const prevH = roomRef.current.h;
      // PR 1's keyboard fit dropped the spacer and put the newest line on the box.
      if (fitRef.current()) { if (pinRef.current === "turnTop") pinRef.current = "bottom"; return; }
      unhideRef.current(prevH);
      settleRef.current(prevH);
    });
    ro.observe(el);
    if (list && isPhoneLayout()) ro.observe(list);   // desktop: only the container, as before
    roomObserverRef.current = { ro, el, list };
  });
  // TRAC-1132 PR 2: know when the customer's finger (or a wheel) drives the list.
  // Passive, and inert off phones (isPhoneTurn gates every reader).
  const touchElRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    const el = embedScrollRef.current;
    if (touchElRef.current === el) return;
    touchElRef.current = el;
    if (!el || typeof el.addEventListener !== "function") return;
    // A tap is not a scroll: only a touch that moved leaves the 300 ms fling window, so the
    // keyboard ride's own scrollTop writes after a tap (TRAC-1152 idle) never read as the
    // customer's and never turn the arrow into a "New message" pill.
    let moved = false;
    const start = (): void => { touchingRef.current = true; moved = false; };
    const move = (): void => { moved = true; userScrollUntilRef.current = Date.now() + 300; };
    const end = (): void => {
      touchingRef.current = false;
      if (moved) userScrollUntilRef.current = Date.now() + 300;
      // Catch anything that arrived while the finger was down, once the fling settles.
      setTimeout(() => { if (!userIsScrollingRef.current()) settleRef.current(); }, 340);
    };
    el.addEventListener("touchstart", start, { passive: true });
    el.addEventListener("touchmove", move, { passive: true });
    el.addEventListener("touchend", end, { passive: true });
    el.addEventListener("touchcancel", end, { passive: true });
    el.addEventListener("wheel", move, { passive: true });
    const lift = (): void => fitTurnToLiftRef.current();   // TRAC-1176: the iPhone keyboard ride's open slide
    el.addEventListener(KB_LIFT_EVENT, lift);
    return () => {
      el.removeEventListener(KB_LIFT_EVENT, lift);
      el.removeEventListener("touchstart", start);
      el.removeEventListener("touchmove", move);
      el.removeEventListener("touchend", end);
      el.removeEventListener("touchcancel", end);
      el.removeEventListener("wheel", move);
      touchElRef.current = null;
    };
  });
  useEffect(() => () => { roomObserverRef.current?.ro.disconnect(); roomObserverRef.current = null; }, []);

  return {
    isInitialScrollPending, setIsInitialScrollPending,
    pendingInitialScroll, setPendingInitialScroll,
    showScrollButton, setShowScrollButton, hasNewMessage,
    messagesEndRef, messagesAreaRef, shouldAutoScrollRef, isProgrammaticScrollRef,
    scrollTimeoutRef, lastScrollTargetRef, lastAssistantMsgRef, lastUserMsgRef,
    lastTurnWrapperRef, lockAutoScrollRef, embedScrollRef,
    getScrollContainer, getScrollTop, getScrollHeight, getViewportHeight, doScrollTo,
    getElementScrollOffset, scheduleProgrammaticScrollEnd, scrollToLatestMessage, fitLastTurnToKeyboard,
    scrollRestoredHistoryIntoView, beginPhoneSend, settleScroll, isPhoneTurn, noteTypingSettled,
    scrollToBottom, isScrolledToBottom, isNearScrollTarget, handleScroll, handleScrollButtonClick,
  };
}
